/**
 * syncEngine.js
 * Offline-first sync manager for React UI Layer.
 */

import * as apiClient from './apiClient.js';

export const KEYS = {
  ACCOUNTS: 'tezer_cache_accounts',
  TRANSACTIONS: 'tezer_cache_transactions',
  GROUPS: 'tezer_cache_groups',
  CATEGORIES: 'tezer_cache_categories',
  FRIENDS: 'tezer_cache_friends',
  PREFERRED_CURRENCIES: 'tezer_cache_preferred_currencies',
  CURRENCY_RATES: 'tezer_cache_currency_rates',
  NOTIFICATIONS: 'tezer_cache_notifications',
  PENDING_ACTIONS: 'tezer_cache_pending_actions',
  INVESTMENTS: 'tezer_cache_investments',
  INVESTMENTS_ANALYTICS: 'tezer_cache_investments_analytics',
  ACCOUNT_PREFERENCES: 'tezer_cache_account_preferences',
  MUTATIONS: 'tezer_sync_queue'
};

let syncStatusListener = null;
let dataRefreshListener = null;

function generateBrowserUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'client-tx-' + Math.random().toString(36).substring(2, 15) + '-' + Date.now().toString(36);
}

export function getLocal(key, defaultValue = []) {
  try {
    let val = localStorage.getItem(key);
    if (!val && key.startsWith('tezer_')) {
      const legacyKey = key.replace('tezer_', 'dhudd_');
      val = localStorage.getItem(legacyKey);
      if (val) {
        localStorage.setItem(key, val);
      }
    }
    return val ? JSON.parse(val) : defaultValue;
  } catch (e) {
    return defaultValue;
  }
}

export function setLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error saving key ${key} to localStorage`, e);
  }
}

export function registerSyncListeners(onSyncStatusChange, onDataRefresh) {
  syncStatusListener = onSyncStatusChange;
  dataRefreshListener = onDataRefresh;

  if (typeof window !== 'undefined') {
    const handleOnline = () => {
      console.log('[Sync Engine] Browser came online. Triggering sync...');
      if (syncStatusListener) syncStatusListener(true);
      syncOfflineQueue().then(() => {
        if (dataRefreshListener) dataRefreshListener();
      });
    };

    const handleOffline = () => {
      console.log('[Sync Engine] Browser went offline.');
      if (syncStatusListener) syncStatusListener(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Return cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
}

export async function syncOfflineQueue() {
  if (!apiClient.getToken() || !apiClient.isOnline()) {
    return false;
  }

  const queue = getLocal(KEYS.MUTATIONS, []);
  if (queue.length === 0) {
    return true;
  }

  console.log(`[Sync Engine] Found ${queue.length} mutations to sync...`);
  try {
    const res = await apiClient.request('/sync/offline', 'POST', { mutations: queue });
    console.log('[Sync Engine] Offline sync response:', res);
    setLocal(KEYS.MUTATIONS, []);
    return true;
  } catch (error) {
    console.error('[Sync Engine] Failed to sync offline queue:', error);
    return false;
  }
}

export async function getAccountsCached() {
  if (apiClient.isOnline() && apiClient.getToken()) {
    try {
      const data = await apiClient.request('/accounts');
      setLocal(KEYS.ACCOUNTS, data);
      return data;
    } catch (e) {
      console.warn('[Sync Engine] Accounts network load failed, loading from cache.', e);
    }
  }
  return getLocal(KEYS.ACCOUNTS, []);
}

export async function getTransactionsCached() {
  if (apiClient.isOnline() && apiClient.getToken()) {
    try {
      const data = await apiClient.request('/transactions');
      setLocal(KEYS.TRANSACTIONS, data);
      return data;
    } catch (e) {
      console.warn('[Sync Engine] Transactions network load failed, loading from cache.', e);
    }
  }
  return getLocal(KEYS.TRANSACTIONS, []);
}

export async function getGroupsCached() {
  if (apiClient.isOnline() && apiClient.getToken()) {
    try {
      const data = await apiClient.request('/groups');
      setLocal(KEYS.GROUPS, data);
      return data;
    } catch (e) {
      console.warn('[Sync Engine] Groups network load failed, loading from cache.', e);
    }
  }
  return getLocal(KEYS.GROUPS, []);
}

export async function createTransactionCached(tx) {
  const isOnlineFlag = apiClient.isOnline() && apiClient.getToken();
  
  const txId = tx.transaction_id || generateBrowserUuid();
  const txWithId = {
    ...tx,
    transaction_id: txId,
    date: tx.date || new Date().toISOString(),
    is_shared: tx.is_shared ? 1 : 0,
    is_recurring: tx.is_recurring ? 1 : 0
  };

  const localTxs = getLocal(KEYS.TRANSACTIONS, []);
  localTxs.unshift(txWithId);
  setLocal(KEYS.TRANSACTIONS, localTxs);

  const localAccounts = getLocal(KEYS.ACCOUNTS, []);
  const acc = localAccounts.find(a => a.account_id === tx.paid_by_account_id);
  const myUserId = localStorage.getItem('dhudd_user_id') || 'self';
  let amt = parseFloat(tx.total_amount) || 0;
  const txPayerId = tx.paid_by_user_id;
  if (txPayerId && txPayerId !== myUserId && txPayerId !== 'self') {
    amt = 0;
  } else if (tx.payer_shares) {
    if (tx.payer_shares[myUserId] !== undefined) amt = parseFloat(tx.payer_shares[myUserId]) || 0;
    else if (tx.payer_shares['self'] !== undefined) amt = parseFloat(tx.payer_shares['self']) || 0;
  }

  if (acc && amt > 0) {
    const isOutflowCategory = (cat) => {
      const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];
      return !INFLOWS.map(n => n.toLowerCase()).includes((cat || '').trim().toLowerCase());
    };

    const outflow = isOutflowCategory(tx.category_name);

    if (acc.type === 'CREDIT_CARD') {
      if (outflow) acc.current_balance = parseFloat((parseFloat(acc.current_balance) + amt).toFixed(4));
      else acc.current_balance = parseFloat((parseFloat(acc.current_balance) - amt).toFixed(4));
    } else {
      if (outflow) acc.current_balance = parseFloat((parseFloat(acc.current_balance) - amt).toFixed(4));
      else acc.current_balance = parseFloat((parseFloat(acc.current_balance) + amt).toFixed(4));
    }
  }

  // Update External Debts in local cache
  if (tx.is_shared && tx.split_details) {
    const extAcc = localAccounts.find(a => a.name && a.name.includes('External Debts'));
    if (extAcc) {
      const fullAmt = parseFloat(tx.total_amount) || 0;
      const members = tx.split_details.members || [];
      const splitCount = members.length || 1;
      const isFriendPaid = tx.paid_by_user_id && tx.paid_by_user_id !== 'self' && tx.paid_by_user_id !== myUserId;

      if (isFriendPaid) {
        let userOwed = fullAmt / splitCount;
        if (tx.split_type === 'EXACT' && tx.split_details.exactAmounts) {
          userOwed = parseFloat(tx.split_details.exactAmounts[myUserId] || 0);
        } else if (tx.split_type === 'PERCENT' && tx.split_details.percentages) {
          const pct = parseFloat(tx.split_details.percentages[myUserId] || 0);
          userOwed = (fullAmt * pct) / 100;
        } else if (tx.split_type === 'SHARES' && tx.split_details.shares) {
          const userShare = parseFloat(tx.split_details.shares[myUserId] || 0);
          const totalShares = Object.values(tx.split_details.shares).reduce((s, v) => s + parseFloat(v), 0) || 1;
          userOwed = (fullAmt * userShare) / totalShares;
        }
        extAcc.current_balance = parseFloat((parseFloat(extAcc.current_balance) - userOwed).toFixed(4));
      } else {
        // User paid
        let payerShare = fullAmt / splitCount;
        if (tx.split_type === 'EXACT' && tx.split_details.exactAmounts) {
          payerShare = parseFloat(tx.split_details.exactAmounts[myUserId] || 0);
        } else if (tx.split_type === 'PERCENT' && tx.split_details.percentages) {
          const pct = parseFloat(tx.split_details.percentages[myUserId] || 0);
          payerShare = (fullAmt * pct) / 100;
        } else if (tx.split_type === 'SHARES' && tx.split_details.shares) {
          const userShare = parseFloat(tx.split_details.shares[myUserId] || 0);
          const totalShares = Object.values(tx.split_details.shares).reduce((s, v) => s + parseFloat(v), 0) || 1;
          payerShare = (fullAmt * userShare) / totalShares;
        }
        const externalReceivable = fullAmt - payerShare;
        extAcc.current_balance = parseFloat((parseFloat(extAcc.current_balance) + externalReceivable).toFixed(4));
      }
    }
  }

  setLocal(KEYS.ACCOUNTS, localAccounts);

  if (isOnlineFlag) {
    try {
      const response = await apiClient.request('/transactions', 'POST', txWithId);
      return response;
    } catch (e) {
      console.warn('[Sync Engine] Online transaction failed:', e);
      const isNetworkErr = !navigator.onLine || (e.message && (e.message.includes('Failed to fetch') || e.message.toLowerCase().includes('network')));
      if (!isNetworkErr) {
        throw e;
      }
      console.warn('[Sync Engine] Network disconnected, adding to offline queue.', e);
    }
  }

  const queue = getLocal(KEYS.MUTATIONS, []);
  queue.push({
    id: generateBrowserUuid(),
    action: 'CREATE_TX',
    payload: txWithId
  });
  setLocal(KEYS.MUTATIONS, queue);
  
  return { transaction_id: txId, offline: true, message: 'Saved offline, queued for sync' };
}

export async function updateTransactionCached(txId, updatedPayload) {
  const isOnlineFlag = apiClient.isOnline() && apiClient.getToken();
  
  // 1. Optimistically update in local transactions cache
  const localTxs = getLocal(KEYS.TRANSACTIONS, []);
  const idx = localTxs.findIndex(t => t.transaction_id === txId);
  if (idx !== -1) {
    localTxs[idx] = { ...localTxs[idx], ...updatedPayload };
    setLocal(KEYS.TRANSACTIONS, localTxs);
  }

  if (isOnlineFlag) {
    try {
      const response = await apiClient.request(`/transactions/${txId}`, 'PUT', updatedPayload);
      return response;
    } catch (e) {
      console.warn('[Sync Engine] Online update transaction failed:', e);
      const isNetworkErr = !navigator.onLine || (e.message && (e.message.includes('Failed to fetch') || e.message.toLowerCase().includes('network')));
      if (!isNetworkErr) {
        throw e;
      }
      console.warn('[Sync Engine] Network disconnected, queueing UPDATE_TX.', e);
    }
  }

  const queue = getLocal(KEYS.MUTATIONS, []);
  queue.push({
    id: generateBrowserUuid(),
    action: 'UPDATE_TX',
    payload: { transaction_id: txId, ...updatedPayload }
  });
  setLocal(KEYS.MUTATIONS, queue);

  return { transaction_id: txId, offline: true, message: 'Updated offline, queued for sync' };
}

export async function deleteTransactionCached(txId) {
  const isOnlineFlag = apiClient.isOnline() && apiClient.getToken();

  // 1. Optimistically remove from local transactions cache
  const localTxs = getLocal(KEYS.TRANSACTIONS, []);
  const filteredTxs = localTxs.filter(t => t.transaction_id !== txId && t.source_transaction_id !== txId);
  setLocal(KEYS.TRANSACTIONS, filteredTxs);

  if (isOnlineFlag) {
    try {
      const response = await apiClient.request(`/transactions/${txId}`, 'DELETE');
      return response;
    } catch (e) {
      console.warn('[Sync Engine] Online delete transaction failed:', e);
      const isNetworkErr = !navigator.onLine || (e.message && (e.message.includes('Failed to fetch') || e.message.toLowerCase().includes('network')));
      if (!isNetworkErr) {
        throw e;
      }
      console.warn('[Sync Engine] Network disconnected, queueing DELETE_TX.', e);
    }
  }

  const queue = getLocal(KEYS.MUTATIONS, []);
  queue.push({
    id: generateBrowserUuid(),
    action: 'DELETE_TX',
    payload: { transaction_id: txId }
  });
  setLocal(KEYS.MUTATIONS, queue);

  return { transaction_id: txId, offline: true, message: 'Deleted offline, queued for sync' };
}

